import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BODY_FIELDS,
  type BodyFieldKey,
  type BodyHistoryEntry,
  type BodyTargets,
} from '@/types/Body';
import { bodyApi, type BodyDto, type BodyTargetsDto } from '@/api/body';
import { bodyPreferencesApi } from '@/api/bodyPreferences';

const ALL_KEYS = BODY_FIELDS.map((f) => f.key);

function dtoToEntry(d: BodyDto): BodyHistoryEntry {
  return {
    date: d.measurementDate,
    weight: d.weightKg ?? undefined,
    height: d.heightCm ?? undefined,
    bicep: d.bicepsCm ?? undefined,
    waist: d.waistCm ?? undefined,
    chest: d.chestCm ?? undefined,
    hip: d.hipsCm ?? undefined,
  };
}

function entryToDto(e: BodyHistoryEntry): BodyDto {
  return {
    measurementDate: e.date,
    weightKg: e.weight ?? null,
    heightCm: e.height ?? null,
    bicepsCm: e.bicep ?? null,
    waistCm: e.waist ?? null,
    chestCm: e.chest ?? null,
    hipsCm: e.hip ?? null,
  };
}

function dtoToTargets(d: BodyTargetsDto): BodyTargets {
  const out: BodyTargets = {};
  if (d.weightKg != null) out.weight = d.weightKg;
  if (d.heightCm != null) out.height = d.heightCm;
  if (d.bicepsCm != null) out.bicep = d.bicepsCm;
  if (d.waistCm != null) out.waist = d.waistCm;
  if (d.chestCm != null) out.chest = d.chestCm;
  if (d.hipsCm != null) out.hip = d.hipsCm;
  return out;
}

function targetsToDto(t: BodyTargets): BodyTargetsDto {
  return {
    weightKg: t.weight ?? null,
    heightCm: t.height ?? null,
    bicepsCm: t.bicep ?? null,
    waistCm: t.waist ?? null,
    chestCm: t.chest ?? null,
    hipsCm: t.hip ?? null,
  };
}

function sanitizeActive(keys: string[] | null | undefined): BodyFieldKey[] {
  if (!keys || keys.length === 0) return [...ALL_KEYS];
  return keys.filter((k): k is BodyFieldKey => ALL_KEYS.includes(k as BodyFieldKey));
}

export function useBody() {
  const [active, setActive] = useState<BodyFieldKey[]>([...ALL_KEYS]);
  const [targets, setTargets] = useState<BodyTargets>({});
  const [history, setHistory] = useState<BodyHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    bodyApi
      .list()
      .then((dtos) => {
        if (!alive) return;
        const entries = dtos
          .map(dtoToEntry)
          .filter((e) => typeof e.date === 'string' && e.date !== '');
        setHistory(entries);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('Failed to load body history from backend', e);
        setHistory([]);
        setError(e instanceof Error ? e.message : 'Failed to load measurements');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    bodyApi
      .getTargets()
      .then((dto) => {
        if (!alive) return;
        setTargets(dtoToTargets(dto));
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('Failed to load body targets from backend', e);
        setTargets({});
      });

    bodyPreferencesApi
      .get()
      .then((dto) => {
        if (!alive) return;
        setActive(sanitizeActive(dto.activeFields ?? null));
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('Failed to load body preferences from backend', e);
      });

    return () => {
      alive = false;
    };
  }, []);

  const latest = useMemo<BodyHistoryEntry>(() => {
    const dated = history.filter((e) => typeof e.date === 'string' && e.date !== '');
    if (dated.length === 0) return { date: '' };
    return [...dated].sort((a, b) => b.date.localeCompare(a.date))[0]!;
  }, [history]);

  const persistActive = useCallback((next: BodyFieldKey[]) => {
    bodyPreferencesApi.save({ activeFields: next }).catch((e) => {
      console.warn('Failed to save body preferences to backend', e);
    });
  }, []);

  const addField = useCallback(
    (key: BodyFieldKey) => {
      setActive((prev) => {
        if (prev.includes(key)) return prev;
        const next = [...prev, key].sort((a, b) => ALL_KEYS.indexOf(a) - ALL_KEYS.indexOf(b));
        persistActive(next);
        return next;
      });
    },
    [persistActive],
  );

  const removeField = useCallback(
    (key: BodyFieldKey) => {
      setActive((prev) => {
        const next = prev.filter((k) => k !== key);
        persistActive(next);
        return next;
      });
    },
    [persistActive],
  );

  const saveMeasurement = useCallback(
    async (entry: BodyHistoryEntry): Promise<{ ok: boolean; message?: string }> => {
      try {
        await bodyApi.create(entryToDto(entry));
      } catch (e) {
        console.warn('Failed to save measurement to backend', e);
        return { ok: false, message: e instanceof Error ? e.message : 'Failed to save' };
      }
      setHistory((prev) => {
        const next = prev.filter((e) => e.date !== entry.date);
        next.push(entry);
        return next;
      });
      return { ok: true };
    },
    [],
  );

  const saveTargets = useCallback(
    async (nextTargets: BodyTargets): Promise<{ ok: boolean; message?: string }> => {
      try {
        await bodyApi.saveTargets(targetsToDto(nextTargets));
        setTargets(nextTargets);
        return { ok: true };
      } catch (e) {
        console.warn('Failed to save targets to backend', e);
        return { ok: false, message: e instanceof Error ? e.message : 'Failed to save' };
      }
    },
    [],
  );

  return {
    active,
    targets,
    history,
    latest,
    loading,
    error,
    addField,
    removeField,
    saveMeasurement,
    saveTargets,
  };
}
